<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <h1 class="title">Supplier Prices</h1>
      <app-cells>
        <app-button @click.native="onShowModal">Add price</app-button>
        <app-input
          @input.native="onSupplierTableSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #name="{ row }">
          <router-link
            :to="{name: 'storage-supplier-prices-detail', params: {id: row.id }}"
            class="table-default__link-border"
          >
            {{ row.trade_name }}
          </router-link>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            @input="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
    <modal
      name="item_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('item_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Create</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Supplier" required>
              <app-select
                @search="onSupplierSearch"
                v-model="form.supplier"
                :options="supplier_options"
                placeholder="Supplier"
                label="trade_name"
                :error="$v.form.supplier.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Item" required>
              <app-select
                @search="onItemSearch"
                @input="onChangeItem"
                v-model="form.item"
                :options="item_options"
                placeholder="Item"
                :error="$v.form.item.$error"
              />
              <template #error>
                <div v-if="$v.form.item.$dirty && !$v.form.item.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Variant">
              <app-select
                v-model="form.variant"
                :reduce="item => item.id"
                :options="variant_options"
                :readonly="!variant_options.length"
                placeholder="Variant"
              />
            </app-form-group>
            <app-form-group label="Price" required>
              <app-fin-number
                v-model="form.price"
                placeholder="Price"
                :error="$v.form.price.$error"
              />
              <template #error>
                <div v-if="$v.form.price.$dirty && !$v.form.price.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit" type="submit">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getStorageItems,
  getStorageSuppliers,
  postStorageSuppliersItemPrices,
  getStorageItemsTypeVariants
} from '@/http'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'StorageSupplierPrices',
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      item_options: [],
      variant_options: [],
      supplier_options: [],
      table: {
        items: [],
        columns: ['name'],
        options: {
          headings: {
            'name': 'Name',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {},
    }
  },
  validations: {
    form: {
      supplier: { required },
      price: { required },
      item: { required },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getStorageSuppliers({
        page: this.pagination.page,
        page_size: 40,
        search: this.filter_search,
        has_prices: true,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
      this.page_loaded = true
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('item_modal')
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postStorageSuppliersItemPrices({
        ...this.form,
        supplier: this.form.supplier.id,
        item: this.form.item.id,
      })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('item_modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onChangeItem(e) {
      this.form.variant = null
      this.variant_options = []
      if (e) {
        getStorageItemsTypeVariants(e.id)
          .then(response => {
            this.variant_options = response.data
          })
      }
    },
    onItemSearch(search, loading) {
      loading(true)
      this.searchItem(loading, search, this)
    },
    searchItem: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.item_options = []
      } else {
        getStorageItems({search}).then(response => {
          vm.item_options = response.data
          loading(false)
        })
      }
    }, 350),
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getStorageSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
    onSupplierTableSearch() {
      this.searchSupplierTable(this)
    },
    searchSupplierTable: debounce((vm) => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
